// "Comeram o que estava planeado?" for a meal that has happened: Sim (as planned, or with other quantities), Não comemos
// (optionally to another day) or Trocámos. The answer settles the meal's stock.
import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, List, ListItemButton, ListItemText, Paper,
  TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import * as api from '../plannerApi';
import * as T from '../types';
import { useNotify } from '../common';
import { dayLabel, mealText, searchKey, slotTitle, today } from '../labels';
import { recipeSubtitle } from './shared';
import { SettleDialog } from './SettleDialog';

const MAX_RESULTS = 8;

/** "Sim" refused: an ingredient's amount is not in the unit it is counted in, so "Efetivar" asks for it (MealConfirmations). */
const SETTLE_QUANTITIES = 'SETTLE_QUANTITIES';

/** Empty meals still to come in the week, to move a dish that was not eaten to. */
function freeSlots(week: T.WeekPlan): T.MealSlot[] {
  const now = today();
  return week.slots
    .filter(s => s.kind === 'Empty' && (s.date > now || (s.date === now && s.mealType === 'Dinner')))
    .sort((a, b) => (a.date === b.date ? (a.mealType === 'Lunch' ? -1 : 1) : a.date < b.date ? -1 : 1));
}

/** The question under a meal still to confirm; `onAnswered` gets the updated week, `onSettled` asks to reload it. */
export function MealConfirmQuestion({ slot, week, onAnswered, onSettled }: {
  slot: T.MealSlot; week: T.WeekPlan; onAnswered: (week: T.WeekPlan) => void; onSettled: () => void;
}) {
  const notify = useNotify();
  const [dialog, setDialog] = useState<'move' | 'swap' | 'adjust' | null>(null);
  const [busy, setBusy] = useState(false);

  const answer = async (body: T.ConfirmMealRequest, success: string) => {
    setDialog(null);
    setBusy(true);
    let updated: T.WeekPlan | undefined;
    try {
      updated = await api.confirmMeal(slot.publicId, body);
    } catch (e) {
      const error = e as api.PlannerError;
      notify(error.message, { severity: error.code === SETTLE_QUANTITIES ? 'info' : 'error' });
      // An amount to say: "Efetivar" for this meal
      if (error.code === SETTLE_QUANTITIES) setDialog('adjust');
    }
    setBusy(false);
    if (!updated) return;
    onAnswered(updated);
    // Swapped for a dish with an amount to say: left to settle, nothing taken yet
    if (body.answer === 'Swapped' && updated.slots.some(s => s.publicId === slot.publicId && !s.confirmedAt)) {
      notify('Trocado; diz quanto gastaste', { severity: 'info' });
      setDialog('adjust');
    } else {
      notify(success, { severity: 'success' });
    }
  };

  const didNotHappen = () => {
    if (freeSlots(week).length > 0) setDialog('move');
    else answer({ answer: 'DidNotHappen' }, 'O stock fica como estava');
  };

  return (
    <Box sx={theme => ({ mx: 1.75, mb: 1, p: 1.25, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.07) })}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>Comeram o que estava planeado?</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        <Button size="small" variant="contained" disabled={busy} onClick={() => answer({ answer: 'AsPlanned' }, 'Efetivado')}>Sim</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => setDialog('adjust')}>Sim, com outras quantidades</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={didNotHappen}>Não comemos</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => setDialog('swap')}>Trocámos</Button>
      </Box>
      {dialog === 'move' && (
        <MoveDialog
          slot={slot} free={freeSlots(week)}
          onClose={target => {
            if (target === undefined) return setDialog(null);
            answer({ answer: 'DidNotHappen', moveToSlotPublicId: target?.publicId ?? null },
              target ? 'Passou para outro dia; o stock fica como estava' : 'O stock fica como estava');
          }}
        />
      )}
      {dialog === 'swap' && (
        <SwapDialog
          onClose={eaten => {
            if (!eaten) return setDialog(null);
            answer({ answer: 'Swapped', ...eaten }, 'Trocado; saiu do stock o que comeram');
          }}
        />
      )}
      {dialog === 'adjust' && (
        <SettleDialog slotIds={[slot.publicId]} onClose={changed => { setDialog(null); if (changed) onSettled(); }} />
      )}
    </Box>
  );
}

/** "Passar para outro dia?": `onClose(undefined)` = cancelled, `null` = leave it not done. */
function MoveDialog({ slot, free, onClose }: {
  slot: T.MealSlot; free: T.MealSlot[]; onClose: (target: T.MealSlot | null | undefined) => void;
}) {
  return (
    <Dialog open onClose={() => onClose(undefined)} maxWidth="xs" fullWidth>
      <DialogTitle>Passar para outro dia?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{slotTitle(slot)}: nada sai do stock.</Typography>
        <Paper variant="outlined">
          <List dense disablePadding>
            <ListItemButton onClick={() => onClose(null)}>
              <ListItemText primary="Não, fica por fazer" />
            </ListItemButton>
            {free.map(s => (
              <ListItemButton key={s.publicId} onClick={() => onClose(s)}>
                <ListItemText primary={dayLabel(s.date)} secondary={mealText(s.mealType)} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(undefined)}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** "O que comeram?": a recipe, or what was typed. Null = cancelled. */
function SwapDialog({ onClose }: { onClose: (eaten: { recipePublicId?: T.Guid; freeText?: string } | null) => void }) {
  const [search, setSearch] = useState('');
  const [recipes, setRecipes] = useState<T.RecipeSummary[] | null>(null);

  React.useEffect(() => {
    api.getRecipes().then(all => setRecipes(all.filter(r => r.course !== 'Side'))).catch(() => setRecipes([]));
  }, []);

  const key = searchKey(search);
  const results = (recipes ?? []).filter(r => key.length === 0 || searchKey(r.name).includes(key)).slice(0, MAX_RESULTS);

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>O que comeram?</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth size="small" autoFocus placeholder="Procurar receita ou escrever (pizza, cantina…)" value={search} onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
            htmlInput: { maxLength: 100 },
          }}
        />
        {recipes === null ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>A carregar receitas…</Typography>
        ) : results.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhuma receita com esse nome.</Typography>
        ) : (
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <List dense disablePadding>
              {results.map(r => (
                <ListItemButton key={r.publicId} onClick={() => onClose({ recipePublicId: r.publicId })}>
                  <ListItemText primary={r.name} secondary={recipeSubtitle(r)} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
        {search.trim() && (
          <Button sx={{ mt: 1 }} onClick={() => onClose({ freeText: search.trim() })}>Comemos «{search.trim()}» (sem receita)</Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
